import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary } from '@treams/treams-hooks';
import { AddIcon, UploadIcon } from '@treams/treams-icons';
import {
	useIdentityProviderSettingsLazyQuery,
	useUpdateIdentityProviderConfigurationMutation,
	useUpdateIdentityProviderMutation,
} from '@treams/treams-network';
import {
	Backdrop,
	Button,
	Container,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Jumbo,
	Label,
	Modal,
	Paragraph,
	Popover,
	Separator,
	SettingsRow,
	SettingsTable,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import SSODeleteModal from './SSODeleteModal';

type IdentityProviderConfiguration = {
	name: string;
	domains: string[];
};

function SSOEditName(props: {
	identityProviderID: string;
	configuration: IdentityProviderConfiguration;
	onUpdated: () => void;
}) {
	const { t } = useTranslation();
	const [name, setName] = useState<string>(props.configuration.name || '');
	const [updateConfiguration, { loading, error: updateError }] =
		useUpdateIdentityProviderConfigurationMutation();
	const [error, attempt, reset] = useErrorBoundary();

	const onSave = attempt(async () => {
		await updateConfiguration({
			variables: {
				input: {
					identityProviderID: props.identityProviderID,
					...props.configuration,
					name: name,
				},
			},
		});
	});

	useEffect(() => {
		setName(props.configuration.name || '');
	}, [props.configuration]);

	useEffect(() => {
		reset();
	}, [name]);

	return (
		<Box mb={4} width={7 / 12}>
			<Heading3>{t('settings.integrations.sso.edit.configuration.name.label')}</Heading3>
			<Paragraph mb={2}>
				{t('settings.integrations.sso.edit.configuration.name.description')}
			</Paragraph>

			<Flex justifyContent="space-between" alignItems="baseline" mb={2}>
				<Box flex={5} mr={3}>
					<Input
						disabled={loading}
						mb={3}
						value={name}
						onChange={(newValue) => {
							setName(newValue);
						}}
					/>
				</Box>
				<Box flex={1}>
					<Button
						title={t('settings.integrations.sso.edit.configuration.save.button')}
						onClick={onSave}
						loading={loading}
						disableSuccess={!!error || !!updateError}
						flexGrow={0}
					/>
				</Box>
			</Flex>

			{error && <GraphQLErrorMessage error={error} />}
		</Box>
	);
}

function SSOEditDomains(props: {
	identityProviderID: string;
	configuration: IdentityProviderConfiguration;
	onUpdated: () => void;
}) {
	const { t } = useTranslation();
	const [adding, setAdding] = useState<boolean>(false);
	const [domainToAdd, setDomainToAdd] = useState<string>('');
	const [updateError, updateAttempt, updateReset] = useErrorBoundary();
	const [deleteError, deleteAttempt, deleteReset] = useErrorBoundary();
	const [updateConfiguration, { loading }] = useUpdateIdentityProviderConfigurationMutation();

	const addDomain = updateAttempt(async () => {
		await updateConfiguration({
			variables: {
				input: {
					identityProviderID: props.identityProviderID,
					...props.configuration,
					domains: [...props.configuration.domains, domainToAdd],
				},
			},
		});
		props.onUpdated();
		setAdding(false);
	});

	const deleteDomain = deleteAttempt(async (domainToDelete: string) => {
		await updateConfiguration({
			variables: {
				input: {
					identityProviderID: props.identityProviderID,
					...props.configuration,
					domains: [
						...props.configuration.domains.filter((d) => {
							return d !== domainToDelete;
						}),
					],
				},
			},
		});
		props.onUpdated();
	});

	useEffect(() => {
		setDomainToAdd('');
	}, [adding]);

	useEffect(() => {
		setDomainToAdd('');
		setAdding(false);
	}, [props.configuration]);

	useEffect(() => {
		updateReset();
	}, [domainToAdd]);

	return (
		<Box mb={4} width={7 / 12}>
			<SettingsTable
				divided
				title={t('settings.integrations.sso.edit.configuration.domain.label')}
				description={t('settings.integrations.sso.edit.configuration.domain.description')}
				toolbar={
					<Popover.Target
						backdrop
						contents={
							<Modal width={400} height="auto">
								<Modal.Header
									title={t('settings.integrations.sso.edit.configuration.domain.add.header')}
								/>
								<Modal.Contents>
									<Label mb={2}>
										{t('settings.integrations.sso.edit.configuration.domain.add.label')}:
									</Label>
									<Input value={domainToAdd} onChange={setDomainToAdd} autoFocus />
									<GraphQLErrorMessage error={updateError} />
								</Modal.Contents>
								<Modal.FormFooter
									disabled={loading}
									onCancel={() => setAdding(false)}
									onSave={addDomain}
								/>
							</Modal>
						}
						visible={adding}
					>
						<Button
							icon={<AddIcon />}
							onClick={() => {
								setAdding(true);
							}}
							type="transparent"
						/>
					</Popover.Target>
				}
			>
				<GraphQLErrorMessage error={deleteError} />
				{props.configuration.domains.map((domain, index) => {
					return (
						<SettingsRow
							loading={loading}
							onDelete={() => {
								deleteReset();
								deleteDomain(domain);
							}}
							key={domain + '-' + index}
						>
							<div>{domain}</div>
						</SettingsRow>
					);
				})}
				{props.configuration.domains.length === 0 && (
					<SettingsRow>
						<div>{t('settings.integrations.sso.edit.configuration.domain.list.empty')}</div>
					</SettingsRow>
				)}
			</SettingsTable>
		</Box>
	);
}

function SSOEditMetadataURL(props: {
	identityProvider: {
		id: string;
		metadataURL?: string | null;
	};
}) {
	const { t } = useTranslation();

	const [metadataURL, setMetadataURL] = useState(props.identityProvider.metadataURL);
	const [update, { error }] = useUpdateIdentityProviderMutation();

	const save = async () => {
		return update({
			variables: { input: { identityProviderID: props.identityProvider.id, metadataURL } },
		});
	};

	return (
		<Box width={7 / 12} mt={`${tokens.gap.section}px`}>
			<Separator />
			<Heading3 mt={`${tokens.gap.section}px`}>
				{t('settings.integrations.sso.edit.replace.title')}
			</Heading3>
			<Paragraph mb={2}>{t('settings.integrations.sso.edit.replace.description')}</Paragraph>

			{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

			<Flex alignItems="center">
				<Box mr={2} flexGrow={1}>
					<Input
						onChange={(newValue) => {
							setMetadataURL(newValue);
						}}
						value={metadataURL ?? ''}
					/>
				</Box>
				<Button title={t('settings.integrations.sso.edit.replace.button')} onClick={save} />
			</Flex>
		</Box>
	);
}

function SSOEditMetadataFile(props: {
	identityProvider: {
		id: string;
		metadataFile?: string | null;
	};
}) {
	const { t } = useTranslation();

	const upload = useRef<HTMLInputElement>(null);
	const [update, { loading, error }] = useUpdateIdentityProviderMutation();

	const save = async () => {
		const files = Array.from(upload.current?.files ?? []);

		if (!files[0]) {
			return;
		}

		await update({
			variables: {
				input: {
					identityProviderID: props.identityProvider.id,
					metadataFile: await files[0].text(),
				},
			},
		});

		upload.current!.value = '';
	};

	return (
		<Box width={7 / 12} mt={`${tokens.gap.section}px`}>
			<Separator />
			<Heading3 mt={`${tokens.gap.section}px`}>
				{t('settings.integrations.sso.edit.replaceFile.title')}
			</Heading3>
			<Paragraph mb={3}>{t('settings.integrations.sso.edit.replaceFile.description')}</Paragraph>

			{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

			<input
				ref={upload}
				type="file"
				accept="text/xml"
				style={{ display: 'none' }}
				onChange={save}
			/>
			<Flex>
				<Button
					icon={<UploadIcon />}
					title={t('settings.integrations.sso.edit.replaceFile.button')}
					onClick={() => upload.current?.click()}
					loading={loading}
				/>
			</Flex>
		</Box>
	);
}

export default function SSOEdit() {
	const { t } = useTranslation();
	const history = useHistory();
	const { addToast } = useToasts();

	const { params } = useRouteMatch<{ id: string }>();
	const [getSettings, { data, error, refetch }] = useIdentityProviderSettingsLazyQuery({
		variables: { id: params.id },
	});

	const [showDeleteModal, setShowDeleteModal] = useState(false);

	useEffect(() => {
		getSettings();
	}, []);

	const onDelete = () => {
		setShowDeleteModal(true);
	};

	const description = () => {
		if (!data) {
			return undefined;
		}

		if (data.identityProvider.configuration?.name) {
			return t('settings.integrations.sso.edit.description', {
				name: data.identityProvider.configuration?.name,
			});
		}

		return t('settings.integrations.sso.edit.description', {
			name: t('settings.integrations.sso.id', { id: data?.identityProvider.id }),
		});
	};

	const configuration: IdentityProviderConfiguration = {
		name: data?.identityProvider.configuration?.name || '',
		domains: data?.identityProvider.configuration?.domains || [],
	};

	return (
		<>
			<Jumbo
				title={t('settings.integrations.sso.edit.title')}
				description={description()}
				footer={<Separator />}
				backButtonLink="/settings/integrations/sso"
				button={<Button title={t('common.delete')} type={'destructive'} onClick={onDelete} />}
			/>
			<Backdrop>
				<Container pt={`${tokens.gap.page}px`} pb={4}>
					{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}
					{!data ? (
						!error && <Spinner centered />
					) : (
						<>
							<>
								<SSOEditName
									identityProviderID={data.identityProvider.id}
									configuration={configuration}
									onUpdated={refetch}
								/>
								<SSOEditDomains
									identityProviderID={data.identityProvider.id}
									configuration={configuration}
									onUpdated={refetch}
								/>
							</>
							{data?.identityProvider.metadataURL && (
								<SSOEditMetadataURL identityProvider={data.identityProvider} />
							)}
							{data?.identityProvider.metadataFile && (
								<SSOEditMetadataFile identityProvider={data.identityProvider} />
							)}
						</>
					)}
				</Container>
			</Backdrop>
			{data && (
				<SSODeleteModal
					visible={!!(showDeleteModal && data)}
					identityProviderID={data.identityProvider.id}
					onCancel={() => {
						setShowDeleteModal(false);
					}}
					onDeleted={() => {
						setShowDeleteModal(false);
						addToast(t('settings.integrations.sso.delete.success'), {
							appearance: 'success',
							autoDismiss: true,
						});
						history.push(`/settings/integrations/sso`);
					}}
				/>
			)}
		</>
	);
}
